import React from 'react';
import { FileText, Wrench, Truck, Smartphone } from 'lucide-react';

const CustomerJourney = () => {
  const steps = [
    {
      step: '01',
      icon: <FileText className="w-10 h-10" />,
      title: 'Get an Estimate',
      description: 'Fill out our online form or give us a call for a free, no-obligation quote'
    },
    {
      step: '02',
      icon: <Wrench className="w-10 h-10" />,
      title: 'Site Visit',
      description: 'We come to your property to assess and plan the perfect lighting solution'
    },
    {
      step: '03',
      icon: <Truck className="w-10 h-10" />,
      title: 'Quick Scheduling',
      description: 'Flexible scheduling options to fit your timeline with professional installation'
    },
    {
      step: '04',
      icon: <Smartphone className="w-10 h-10" />,
      title: 'Enjoy Your New Lights',
      description: 'Control your lights from your phone and enjoy brilliant lighting year-round'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <h2 className="text-5xl sm:text-6xl font-bold text-white mb-6">
            It's As Simple As
          </h2>
          <div className="w-24 h-1 bg-blue-500 mx-auto rounded-full"></div>
        </div>

        <div className="grid md:grid-cols-4 gap-6 max-w-7xl mx-auto mb-20">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              {/* Step Card */}
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700/50 hover:border-blue-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20 group hover:-translate-y-3">
                {/* Step Number Badge */}
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-lg">{step.step}</span>
                </div>
                
                {/* Icon Container */}
                <div className="mb-6 relative">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-blue-500/30">
                    <div className="text-blue-400 group-hover:text-blue-300 transition-colors duration-300">
                      {step.icon}
                    </div>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-4 text-center">{step.title}</h3>
                <p className="text-gray-400 text-center leading-relaxed">{step.description}</p>
              </div>

              {/* Arrow Connector */}
              {index < steps.length - 1 && (
                <div className="hidden md:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-20 items-center">
                  <div className="flex items-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl p-12 border border-gray-700/50 max-w-4xl mx-auto">
            <p className="text-gray-300 text-2xl mb-8 font-light">
              Ready to get started? Let's brighten up your property!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:306-250-4647"
                className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold rounded-xl transition-all duration-200 shadow-lg hover:shadow-green-500/50 hover:scale-105 text-lg"
              >
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call Now: 306-250-4647
              </a>
              <button
                onClick={() => document.getElementById('quote').scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-xl transition-all duration-200 shadow-lg hover:shadow-blue-500/50 hover:scale-105 text-lg"
              >
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Request a Quote
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerJourney;
